import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Descriptions,
  Divider,
  Space,
} from 'antd';
import { KeyOutlined, UserOutlined } from '@ant-design/icons';
import { getUser, updateSelfPassword, User } from '../../services/user';
import './index.css';

const Profile: React.FC = () => {
  const [userInfo, setUserInfo] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [passwordForm] = Form.useForm();

  // 加载用户信息
  useEffect(() => {
    loadUserInfo();
  }, []);

  const loadUserInfo = async () => {
    try {
      const info = localStorage.getItem('user');
      if (info) {
        const parsedInfo = JSON.parse(info);
        // 从服务器获取最新的用户信息
        const res = await getUser(parsedInfo.id);
        const data = res.data || res;
        setUserInfo(data);
      }
    } catch (error: any) {
      // 全局 Alert 已自动显示错误
    }
  };

  // 修改密码
  const handleChangePassword = async (values: {
    oldPassword: string;
    newPassword: string;
  }) => {
    setLoading(true);
    try {
      await updateSelfPassword(values.oldPassword, values.newPassword);
      message.success('密码修改成功，请重新登录');
      passwordForm.resetFields();
      // 清除登录信息并跳转到登录页
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }, 1500);
    } catch (error: any) {
      // 全局 Alert 已自动显示错误
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 个人信息卡片 */}
        <Card
          title={
            <span>
              <UserOutlined /> 个人信息
            </span>
          }
        >
          {userInfo && (
            <Descriptions column={2} bordered>
              <Descriptions.Item label="用户ID">{userInfo.id}</Descriptions.Item>
              <Descriptions.Item label="手机号">{userInfo.phone}</Descriptions.Item>
              <Descriptions.Item label="姓名">
                {userInfo.name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="角色">
                {userInfo.role === 'admin' ? '管理员' : '普通用户'}
              </Descriptions.Item>
              <Descriptions.Item label="单位" span={2}>
                {userInfo.company || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="地址" span={2}>
                {userInfo.address || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="最后登录时间" span={2}>
                {userInfo.lastLoginAt
                  ? new Date(userInfo.lastLoginAt).toLocaleString('zh-CN')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="最后登录IP" span={2}>
                {userInfo.lastLoginIp || '-'}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Card>

        {/* 修改密码卡片 */}
        <Card
          title={
            <span>
              <KeyOutlined /> 修改密码
            </span>
          }
        >
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handleChangePassword}
            style={{ maxWidth: 600 }}
          >
            <Form.Item
              label="旧密码"
              name="oldPassword"
              rules={[{ required: true, message: '请输入旧密码' }]}
            >
              <Input.Password placeholder="请输入旧密码" />
            </Form.Item>
            <Form.Item
              label="新密码"
              name="newPassword"
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 6, message: '密码至少6位' },
              ]}
            >
              <Input.Password placeholder="请输入新密码（至少6位）" />
            </Form.Item>
            <Form.Item
              label="确认密码"
              name="confirmPassword"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: '请确认新密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="请再次输入新密码" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                修改密码
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Space>
    </div>
  );
};

export default Profile;
